/**
 * useConsoleScroll — Shared scroll/wrap/copy behavior for console modals.
 *
 * Manages auto-scroll, user-scroll detection, wrap toggle, and clipboard copy
 * so each console modal doesn't need to duplicate this logic.
 */
import { useCallback, useEffect, useRef, useState } from "react";

type UseConsoleScrollReturn = {
  consoleRef: React.RefObject<HTMLDivElement>;
  wrapLines: boolean;
  setWrapLines: React.Dispatch<React.SetStateAction<boolean>>;
  autoScroll: boolean;
  handleScroll: () => void;
  scrollToBottom: () => void;
  handleCopyOutput: (lines: string[]) => void;
};

export const useConsoleScroll = (scrollTrigger: number): UseConsoleScrollReturn => {
  const [wrapLines, setWrapLines] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const consoleRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);

  // Auto-scroll to bottom when scrollTrigger changes (caller passes lines.length)
  useEffect(() => {
    if (!autoScroll || !consoleRef.current) return;
    consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
  }, [scrollTrigger, autoScroll]);

  const handleScroll = useCallback(() => {
    const el = consoleRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (!atBottom && !userScrolledRef.current) {
      userScrolledRef.current = true;
      setAutoScroll(false);
    } else if (atBottom && userScrolledRef.current) {
      userScrolledRef.current = false;
      setAutoScroll(true);
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    userScrolledRef.current = false;
    setAutoScroll(true);
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, []);

  const handleCopyOutput = useCallback((lines: string[]) => {
    const text = lines.join("\n");
    void navigator.clipboard.writeText(text);
  }, []);

  return {
    consoleRef: consoleRef as React.RefObject<HTMLDivElement>,
    wrapLines,
    setWrapLines,
    autoScroll,
    handleScroll,
    scrollToBottom,
    handleCopyOutput,
  };
};
